import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { Alert, Button, Typography } from '@/ui';

import { AssistantAppointmentCard } from './AssistantAppointmentCard';
import {
  AssistantPageShell,
  AssistantSignOutButton,
} from './AssistantPageShell';
import {
  assistantErrorStatus,
  useGetAssistantAppointmentQuery,
  useGetAssistedMembersQuery,
} from './api';
import {
  hasUnsettledAssistantOperation,
  useAssistantOperations,
} from './operationRecords';
import { assistantMemberPath } from './paths';
import { readAssistantSession, useAssistantSessionToken } from './session';
import {
  ASSISTANT_HOME_PATH,
  useEndAssistantSessionOnUnauthorized,
  useSignOutAssistant,
} from './useAssistantSession';

import type { AssistantAction, AssistantAppointment } from './api';
import type { AssistantCommandProblem } from './useAssistantCommand';
import type { FC, ReactNode } from 'react';

export interface AssistantActionContext {
  appointment: AssistantAppointment;
  memberId: string;
  memberName: string;
}

const NOT_ALLOWED: Record<AssistantAction, string> = {
  cancel:
    'This session can’t be cancelled online any more. Email concierge@mento.co for help.',
  reschedule:
    'This session can’t be rescheduled online any more. Email concierge@mento.co for help.',
};

export const AssistantCommandProblemAlert: FC<{
  action: AssistantAction;
  problem: AssistantCommandProblem | null;
}> = ({ action, problem }) => {
  if (!problem) return null;
  switch (problem.kind) {
    case 'changed':
      return (
        <Alert role="alert" severity="warning">
          This session changed since you opened it. Check the details above and
          confirm again.
        </Alert>
      );
    case 'not_allowed':
      return (
        <Alert role="alert" severity="error">
          {NOT_ALLOWED[action]}
        </Alert>
      );
    case 'failed':
    default:
      if (problem.code === 'storage_unavailable') {
        return (
          <Alert role="alert" severity="error">
            This browser isn&apos;t letting Mento save the request, so nothing
            was sent. Allow site data for Mento, or use another browser.
          </Alert>
        );
      }
      return (
        <Alert role="alert" severity="error">
          We couldn&apos;t check this session just now. Try again.
        </Alert>
      );
  }
};

const ActionBody: FC<{
  action: AssistantAction;
  appointmentId: string;
  children: (context: AssistantActionContext) => ReactNode;
  memberId: string;
}> = ({ action, appointmentId, children, memberId }) => {
  const token = useAssistantSessionToken();
  const members = useGetAssistedMembersQuery(token ? undefined : skipToken);
  const detail = useGetAssistantAppointmentQuery(
    token ? { appointmentId, memberId } : skipToken,
    // Always the current versions: they are the command's preimage.
    { refetchOnMountOrArgChange: true },
  );
  const { refetch: refetchDetail } = detail;
  const records = useAssistantOperations(memberId);
  const { isSigningOut, signOut } = useSignOutAssistant();
  useEndAssistantSessionOnUnauthorized(members.error, detail.error);

  const memberName =
    members.currentData?.find((member) => member.memberId === memberId)
      ?.displayName ?? 'this person';
  const backLink = (
    <Link className="underline" to={assistantMemberPath(memberId)}>
      Back to {memberName}&apos;s sessions
    </Link>
  );
  const status = assistantErrorStatus(detail.error);

  let body: ReactNode;
  if (status === 403 || status === 404) {
    body = (
      <div className="flex flex-col gap-3" role="alert">
        <Typography variant="body1">
          {status === 403
            ? 'You’re not a scheduling assistant for this person anymore.'
            : 'We couldn’t find this session.'}
        </Typography>
        <div>
          <Link className="underline" to={ASSISTANT_HOME_PATH}>
            Back to the people you assist
          </Link>
        </div>
      </div>
    );
  } else if (detail.isError) {
    body = (
      <div className="flex flex-col gap-3">
        <Typography variant="body1">
          We couldn&apos;t load this session.
        </Typography>
        <div>
          <Button variant="outlined" onClick={refetchDetail}>
            Retry
          </Button>
        </div>
      </div>
    );
  } else if (!detail.currentData) {
    body = <Typography variant="body1">Loading…</Typography>;
  } else if (hasUnsettledAssistantOperation(records, appointmentId)) {
    // No second command while one has no answer: finish that one first.
    body = (
      <div className="flex flex-col gap-3" role="status">
        <Typography variant="body1">
          A change to this session is still in progress.
        </Typography>
        <div>{backLink}</div>
      </div>
    );
  } else {
    const appointment = detail.currentData;
    const allowed =
      action === 'reschedule'
        ? appointment.capabilities.canRescheduleOccurrence
        : appointment.capabilities.canCancelOccurrence;
    body = (
      <div className="flex flex-col gap-4">
        <AssistantAppointmentCard appointment={appointment} />
        {allowed ? (
          children({ appointment, memberId, memberName })
        ) : (
          <Alert severity="info">{NOT_ALLOWED[action]}</Alert>
        )}
        <div>{backLink}</div>
      </div>
    );
  }

  return (
    <AssistantPageShell
      headerActions={
        <AssistantSignOutButton disabled={isSigningOut} onSignOut={signOut} />
      }
    >
      {body}
    </AssistantPageShell>
  );
};

/**
 * The frame of the reschedule and cancel pages: the assistant's session, the
 * appointment's current detail, and the rule that a command still waiting for
 * an answer blocks another one for the same appointment.
 */
export const AssistantActionPage: FC<{
  action: AssistantAction;
  children: (context: AssistantActionContext) => ReactNode;
}> = ({ action, children }) => {
  const { appointmentId, memberId } = useParams();
  // Checked on arrival only, as on the member page.
  const arrivedWithSession = useMemo(() => readAssistantSession() !== null, []);

  if (!memberId || !appointmentId || !arrivedWithSession) {
    return <Navigate to={ASSISTANT_HOME_PATH} replace />;
  }
  return (
    <ActionBody
      key={`${memberId}:${appointmentId}`}
      action={action}
      appointmentId={appointmentId}
      memberId={memberId}
    >
      {children}
    </ActionBody>
  );
};
